import {Cached} from '@utils';
import {CycleListType} from './cycle-list.type';
import {CycleStatusType} from '../../../../../entities';

export type ICycleListTypeData = {
	label: string; // translation key: cycle.list-type.[CycleListType].label
	icon: string;
	tooltip: string; // translation key: cycle.list-type.[CycleListType].tooltip
	associatedCycleStatuses: CycleStatusType[]; // cycle statuses associated with this list type
	associatedListTypes: CycleListType[]; // by default, the list type is associated with itself
};

export type ICycleListCategory = {
	label: string; // translation key?: cycle.list-type-category.[CycleListCategory].label
	icon: string;
	tooltip: string; // translation key?: cycle.list-type-category.[CycleListCategory].tooltip
	types: Partial<Record<CycleListType, ICycleListTypeData>>;
};

export enum CycleListCategoryType {
	PROCESS = 'PROCESS',
	SIGNED = 'SIGNED',
	COMPLETED = 'COMPLETED',
	TEMPLATES_AND_DRAFTS = 'TEMPLATES_AND_DRAFTS',
	Uncategorized = undefined,
}

export class CycleListData {
	static getCycleListLabel(type: CycleListType): string {
		return 'cycle.list-type.' + type.toLowerCase() + '.label';
	}

	static getCycleListTooltip(type: CycleListType): string {
		return 'cycle.list-type.' + type.toLowerCase() + '.tooltip';
	}

	@Cached()
	static getCycleListCategoriesData(): Record<CycleListCategoryType, ICycleListCategory> {
		return {
			// undefined means - cycle list types uncategorized
			[CycleListCategoryType.Uncategorized]: {
				label: undefined,
				icon: undefined,
				tooltip: undefined,
				types: {
					[CycleListType.All]: {
						label: CycleListData.getCycleListLabel(CycleListType.All),
						icon: 'pi pi-file',
						tooltip: CycleListData.getCycleListTooltip(CycleListType.All),
						associatedCycleStatuses: [],
						associatedListTypes: [CycleListType.InProgress, CycleListType.Waiting, CycleListType.Finished, CycleListType.Signed, CycleListType.Rejected, CycleListType.Canceled, CycleListType.Archive],
					},
				},
			},
			[CycleListCategoryType.PROCESS]: {
				label: 'cycle.list-type-category.process.label',
				icon: 'pi pi-folder',
				tooltip: 'cycle.list-type-category.process.tooltip',
				types: {
					[CycleListType.Waiting]: {
						label: CycleListData.getCycleListLabel(CycleListType.Waiting),
						icon: 'pi pi-file',
						tooltip: CycleListData.getCycleListTooltip(CycleListType.Waiting),
						associatedCycleStatuses: [],
						associatedListTypes: [CycleListType.Waiting],
					},
					[CycleListType.Future]: {
						label: CycleListData.getCycleListLabel(CycleListType.Future),
						icon: 'pi pi-file',
						tooltip: CycleListData.getCycleListTooltip(CycleListType.Future),
						associatedCycleStatuses: [CycleStatusType.Cycling],
						associatedListTypes: [CycleListType.Future],
					},
					[CycleListType.InProgress]: {
						label: CycleListData.getCycleListLabel(CycleListType.InProgress),
						icon: 'pi pi-file',
						tooltip: CycleListData.getCycleListTooltip(CycleListType.InProgress),
						associatedCycleStatuses: [CycleStatusType.Cycling],
						associatedListTypes: [CycleListType.InProgress],
					},
					[CycleListType.SentForm]: {
						label: CycleListData.getCycleListLabel(CycleListType.SentForm),
						icon: 'pi pi-file',
						tooltip: CycleListData.getCycleListTooltip(CycleListType.SentForm),
						associatedCycleStatuses: [],
						associatedListTypes: [CycleListType.SentForm],
					},
				},
			},
			[CycleListCategoryType.SIGNED]: {
				label: 'cycle.list-type-category.signed.label',
				icon: 'pi pi-check-square',
				tooltip: 'cycle.list-type-category.signed.tooltip',
				types: {
					[CycleListType.Signed]: {
						label: CycleListData.getCycleListLabel(CycleListType.Signed),
						icon: 'pi pi-file',
						tooltip: CycleListData.getCycleListTooltip(CycleListType.Signed),
						associatedCycleStatuses: [],
						associatedListTypes: [CycleListType.Signed],
					},
					[CycleListType.SignedApproved]: {
						label: CycleListData.getCycleListLabel(CycleListType.SignedApproved),
						icon: 'pi pi-file',
						tooltip: CycleListData.getCycleListTooltip(CycleListType.SignedApproved),
						associatedCycleStatuses: [],
						associatedListTypes: [CycleListType.SignedApproved],
					},
					[CycleListType.SignedAlert]: {
						label: CycleListData.getCycleListLabel(CycleListType.SignedAlert),
						icon: 'pi pi-file',
						tooltip: CycleListData.getCycleListTooltip(CycleListType.SignedAlert),
						associatedCycleStatuses: [],
						associatedListTypes: [CycleListType.SignedAlert],
					},
					[CycleListType.SignedRejected]: {
						label: CycleListData.getCycleListLabel(CycleListType.SignedRejected),
						icon: 'pi pi-file',
						tooltip: CycleListData.getCycleListTooltip(CycleListType.SignedRejected),
						associatedCycleStatuses: [],
						associatedListTypes: [CycleListType.SignedRejected],
					},
					[CycleListType.SignedTimeout]: {
						label: CycleListData.getCycleListLabel(CycleListType.SignedTimeout),
						icon: 'pi pi-file',
						tooltip: CycleListData.getCycleListTooltip(CycleListType.SignedTimeout),
						associatedCycleStatuses: [],
						associatedListTypes: [CycleListType.SignedTimeout],
					},
				},
			},
			[CycleListCategoryType.COMPLETED]: {
				label: 'cycle.list-type-category.completed.label',
				icon: 'pi pi-check-circle',
				tooltip: 'cycle.list-type-category.completed.tooltip',
				types: {
					[CycleListType.Finished]: {
						label: CycleListData.getCycleListLabel(CycleListType.Finished),
						icon: 'pi pi-file',
						tooltip: CycleListData.getCycleListTooltip(CycleListType.Finished),
						associatedCycleStatuses: [CycleStatusType.Completed, CycleStatusType.Completing],
						associatedListTypes: [CycleListType.Finished],
					},
					[CycleListType.Completing]: {
						label: CycleListData.getCycleListLabel(CycleListType.Completing),
						icon: 'pi pi-file',
						tooltip: CycleListData.getCycleListTooltip(CycleListType.Completing),
						associatedCycleStatuses: [CycleStatusType.Completing],
						associatedListTypes: [CycleListType.Completing],
					},
					[CycleListType.Rejected]: {
						label: CycleListData.getCycleListLabel(CycleListType.Rejected),
						icon: 'pi pi-file',
						tooltip: CycleListData.getCycleListTooltip(CycleListType.Rejected),
						associatedCycleStatuses: [CycleStatusType.Denied, CycleStatusType.Denying],
						associatedListTypes: [CycleListType.Rejected],
					},
					[CycleListType.Canceled]: {
						label: CycleListData.getCycleListLabel(CycleListType.Canceled),
						icon: 'pi pi-file',
						tooltip: CycleListData.getCycleListTooltip(CycleListType.Canceled),
						associatedCycleStatuses: [CycleStatusType.Canceled, CycleStatusType.Canceling, CycleStatusType.Expired],
						associatedListTypes: [CycleListType.Canceled],
					},
					[CycleListType.Archive]: {
						label: CycleListData.getCycleListLabel(CycleListType.Archive),
						icon: 'pi pi-file',
						tooltip: CycleListData.getCycleListTooltip(CycleListType.Archive),
						associatedCycleStatuses: [],
						associatedListTypes: [CycleListType.Archive],
					},
				},
			},
			[CycleListCategoryType.TEMPLATES_AND_DRAFTS]: {
				label: 'cycle.list-type-category.templates.label',
				icon: 'pi pi-copy',
				tooltip: 'cycle.list-type-category.templates.tooltip',
				types: {
					[CycleListType.Draft]: {
						label: CycleListData.getCycleListLabel(CycleListType.Draft),
						icon: 'pi pi-file',
						tooltip: CycleListData.getCycleListTooltip(CycleListType.Draft),
						associatedCycleStatuses: [CycleStatusType.Draft],
						associatedListTypes: [CycleListType.Draft],
					},
					[CycleListType.PrivateTemplate]: {
						label: CycleListData.getCycleListLabel(CycleListType.PrivateTemplate),
						icon: 'pi pi-file',
						tooltip: CycleListData.getCycleListTooltip(CycleListType.PrivateTemplate),
						associatedCycleStatuses: [CycleStatusType.PrivateTemplate],
						associatedListTypes: [CycleListType.PrivateTemplate],
					},
					[CycleListType.PublicTemplate]: {
						label: CycleListData.getCycleListLabel(CycleListType.PublicTemplate),
						icon: 'pi pi-file',
						tooltip: CycleListData.getCycleListTooltip(CycleListType.PublicTemplate),
						associatedCycleStatuses: [CycleStatusType.PublicTemplate],
						associatedListTypes: [CycleListType.PublicTemplate],
					},
					[CycleListType.PrivateForm]: {
						label: CycleListData.getCycleListLabel(CycleListType.PrivateForm),
						icon: 'pi pi-file',
						tooltip: CycleListData.getCycleListTooltip(CycleListType.PrivateForm),
						associatedCycleStatuses: [CycleStatusType.PrivateForm],
						associatedListTypes: [CycleListType.PrivateForm],
					},
					[CycleListType.PublicForm]: {
						label: CycleListData.getCycleListLabel(CycleListType.PublicForm),
						icon: 'pi pi-file',
						tooltip: CycleListData.getCycleListTooltip(CycleListType.PublicForm),
						associatedCycleStatuses: [CycleStatusType.PublicForm],
						associatedListTypes: [CycleListType.PublicForm],
					},
				},
			},
		};
	}

	@Cached()
	static getCycleListTypesData(): Record<CycleListType, ICycleListTypeData> {
		const categories = CycleListData.getCycleListCategoriesData();
		const flattenedTypes: Record<CycleListType, ICycleListTypeData> = {} as Record<CycleListType, ICycleListTypeData>;
		for (const category of Object.values(categories)) {
			for (const [type, data] of Object.entries(category.types)) {
				flattenedTypes[type as CycleListType] = data;
			}
		}
		return flattenedTypes;
	}
}
